import React, { useContext, useEffect, useRef, useState } from 'react'
import { SafeAreaView } from 'react-native-safe-area-context'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { Text } from '@src/components/base/Text'
import { BottomSheet } from '@src/components/BottomSheet'
import { IconButton } from '@components/base'
import { CoachesCoachingKind, SurveysConfigKind, UiStateNames } from '@src/types'
import { StyleService, useStyleSheet } from '@src/style/service'
import { NavigatorContext, Storage, useDispatchAsync } from '@src/utils'
import { AppStackParamList } from '@src/navigation/types'
import { useSnack } from '@src/utils/navigatorContext'
import { appointmentKindSelector, userSelector } from '@src/selectors/app'
import { CoachAvatar } from '@src/components/Avatar/CoachAvatar'

export const RateCoachModal = () => {
  const styles = useStyleSheet(themedStyles)
  const showSnack = useSnack()
  const user = useSelector(userSelector)
  const route = useRoute<RouteProp<AppStackParamList, 'RateCoachModal'>>()
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const submittingRef = useRef(false)

  const [rating, setRating] = useState(0)

  const coach = user?.lastOccurredCoachingAppointment?.providers[0]?.coach
  const { appointmentId } = route.params

  const lastAppointmentCoachingKind = useSelector(
    appointmentKindSelector(user?.lastOccurredCoachingAppointment?.kind),
  )?.coachingKind
  const coachingKind = lastAppointmentCoachingKind ?? user?.primaryCoachingKind

  useEffect(
    () => () => {
      if (submittingRef.current) {
        disableLoader()
        submittingRef.current = false
      }
    },
    [disableLoader],
  )

  if (!appointmentId || !coach) {
    return null
  }

  const { fullName, photo } = coach

  const postCallSurvey =
    coachingKind?.key === CoachesCoachingKind.HealthCoaching
      ? SurveysConfigKind.HealthCoachingPostCallFeedback
      : SurveysConfigKind.PostCallFeedback
  const postCallLowRatingSurvey =
    coachingKind?.key === CoachesCoachingKind.HealthCoaching
      ? SurveysConfigKind.HealthCoachingPostCallFeedbackLowRating
      : SurveysConfigKind.PostCallFeedbackLowRating

  const handlePress = async (index: number) => {
    if (submittingRef.current) {
      return
    }

    submittingRef.current = true
    enableLoader()

    const rating = index + 1
    setRating(rating)

    try {
      await dispatchAsync({
        type: 'nutritionistHub/rateCoach',
        payload: { appointmentId, rating, coachUserId: coach.coachId },
      })
      await dispatchAsync({
        type: 'users/fetch',
      })

      Storage.set(`${Storage.LAST_COACH_RATING_KEY}_${user.id}`, rating)

      if (rating < 4) {
        navigation.replace('Questionnaire', {
          questionnaire: postCallLowRatingSurvey,
        })
      } else {
        navigation.replace('Questionnaire', { questionnaire: postCallSurvey })
      }
    } catch {
      showSnack('Failed to rate the coach. Please try again.', null, 'error')
    } finally {
      disableLoader()
      submittingRef.current = false
    }
  }

  const handleOnDismiss = () => {
    Storage.set(`${Storage.COACH_RATING_DISMISSED_KEY}_${appointmentId}`, true)
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.CoachRatingModalDismissed,
        value: true,
      },
    })
  }

  return (
    <BottomSheet showDismissButton onDismiss={handleOnDismiss}>
      <SafeAreaView edges={['bottom']} style={styles.container}>
        <CoachAvatar photo={photo} style={styles.coachPhoto} />

        <Text type="title-2" style={styles.title}>
          How satisfied are you with your call with {fullName}?
        </Text>

        <View style={styles.starsContainer}>
          {Array.from(new Array(5)).map((_, index) => {
            return (
              <IconButton
                type="transparent"
                key={index}
                onPress={() => handlePress(index)}
                iconName="star"
                iconProps={{
                  weight: 'fill',
                  style: rating > index ? styles.selected : styles.notSelected,
                }}
                accessibilityLabel="star-rate"
                size="xl"
              />
            )
          })}
        </View>

        <Text type="small" style={styles.ratingGuide}>
          Not helpful (1 star) to extremely helpful (5 star)
        </Text>
      </SafeAreaView>
    </BottomSheet>
  )
}

const themedStyles = StyleService.create({
  container: {
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  coachPhoto: {
    width: 80,
    height: 80,
    borderRadius: 48,
    marginVertical: 16,
  },
  spinner: {
    color: 'theme.surface.base',
  },
  title: {
    textAlign: 'center',
    marginBottom: 16,
  },
  starsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  notSelected: {
    color: 'theme.surface.base1',
  },
  selected: {
    color: 'theme.marketing.yellow',
  },
  ratingGuide: {
    marginTop: 24,
  },
})
